/**
 * Die Rechnung hinter dem **Drag Handle** — wohin ein Zug an der Kante die
 * Grenze zwischen **Aufgabenliste** und **Projekt-Canvas** legt, und wann er
 * dabei zwischen **geteilt** und **maximiert** umschaltet. Siehe „Drag Handle"
 * in CONTEXT.md.
 *
 * Bewusst ohne React- und DOM-Bezug, wie die übrigen `utils/`: Die Grenzfälle
 * — und davon hat diese Geste mehr als die meisten — sollen ohne gerendertes
 * Panel prüfbar sein.
 */

import type { CanvasPanelState } from '../ProjectCanvas.types'

/** Halbe/halbe. Der Wert, der gilt, solange nie gezogen wurde. */
export const DEFAULT_SPLIT_RATIO = 0.5

/**
 * Mindestbreite des **Canvas** im geteilten Zustand — steht auch als `min-width` im SCSS.
 *
 * Bis #296 galt sie für beide Spalten. Die **Aufgabenliste** hat seither ihre eigene,
 * größere ({@link LIST_MIN_WIDTH}): Mit 320px verschwanden im Kopf die Buttons.
 */
export const MIN_COLUMN_WIDTH = 320

/**
 * Mindestbreite der **Aufgabenliste** neben der Pinnwand (#296, Sven 09.10.2026): So schmal, dass im Kopf
 * Icon (bzw. 90px erste Titelzeile) und die Buttons ohne Beschriftung noch nebeneinander stehen — vorher
 * schrumpfte sie bis 320px, und die Buttons verschwanden auf dem Weg dorthin.
 *
 * Gemessen wie das Verhältnis: vom linken Rand der Zeile bis an den Canvas, also mit dem 7px-Rand der
 * Spalte (`ProjectPage.scss`, `--canvas-open`). 68 Einzug + 90 Icon samt Griff + 24 Abstand + 171 Buttons
 * (Stern 32, Liste | Stapel 79, Details 36, 2 × 12; gemessen in der Story `CanvasGeteiltSchmal`) + 68 rechts
 * (62 Polster + 6 Scrollbar-Rand) + 7 Rand = 428, plus 1px gegen Subpixel. Dieselbe Zahl als `$list-min-width` in `ProjectCanvasPanel.scss` und als
 * `PROJECT_LIST_MIN_WIDTH` in `navigationVisibility.ts` (ein Unit-Test hält sie zusammen).
 */
export const LIST_MIN_WIDTH = 429

/**
 * Darunter klappt die **Aufgabenliste** im Zug zu (der Canvas wird **maximiert**): ihre halbe Mindestbreite —
 * dieselbe Regel wie {@link COLLAPSE_WIDTH} für den Canvas (CONTEXT.md, Drag Handle: „Beide Schwellen liegen
 * bei der halben Mindestbreite der jeweiligen Seite“).
 */
export const LIST_COLLAPSE_WIDTH = LIST_MIN_WIDTH / 2

/**
 * Darunter ist eindeutig gemeint, dass der **Canvas** weg soll: Lässt man ihn
 * darunter los, wird er **geschlossen** (`commitSplitDrag` — im Zug selbst
 * folgt er der Hand bis auf den Boden, siehe `trackSplitDrag`). Für die
 * **Aufgabenliste** gilt dasselbe mit ihrer eigenen Zahl
 * ({@link LIST_COLLAPSE_WIDTH}).
 *
 * Die halbe Mindestbreite, und der Abstand zu ihr ist der eigentliche Zweck.
 * Läge die Schwelle ebenfalls bei 320, flatterte der Zustand: Ein Zeiger, der
 * auf der Grenze steht, ließe die Liste im Wechsel herein- und herausfahren
 * (0,25s) und die Breadcrumb dazu blinken. Mit zwei Schwellen muss man die
 * Grenze bewusst deutlich unterschreiten.
 *
 * Sie gilt in BEIDE Richtungen und macht die Geste damit umkehrbar: Wer den
 * Canvas zuzieht und im selben Zug wieder nach links geht, bekommt ihn zurück.
 * Das Band zwischen 160 und 320 ist breit genug, dass daraus kein Flattern
 * wird. (Bis ADR 0010 war das anders — `closed` war eine Sackgasse, weil es
 * dort kein Drag Handle mehr gab. Seit es eines gibt, wäre ein Zug, der ins
 * Leere läuft, ein Hänger und kein Schutz.)
 */
export const COLLAPSE_WIDTH = 160

/**
 * Der Abstand des MAXIMIERTEN Canvas zur Kartenkante links.
 *
 * Steht als `$canvas-container-gap` in `ProjectCanvasPanel.scss` und wird
 * gebraucht, um die Breite vorherzusagen, die der Canvas nach der Bewegung
 * haben WIRD (`predictCanvasContentWidth`). Ein Invarianten-Test hält beide
 * Zahlen zusammen.
 */
export const CANVAS_CONTAINER_GAP = 8

/**
 * Wie lange ein Größenwechsel animiert.
 *
 * Muss zur `transition` von `.project-canvas-panel` passen
 * (`ProjectCanvasPanel.scss`, 0,25s); dort steht ein Verweis zurück. Zwei
 * Stellen brauchen die Zahl: Der Zug setzt so lange nicht hart nach
 * (`useCanvasSplitDrag`), und das Panel meldet den geschlossenen Zustand erst
 * danach nach oben (`ProjectCanvasPanel`), damit der Canvas zuschrumpft statt
 * zu verschwinden.
 */
export const RESIZE_ANIMATION_MS = 250

/**
 * Der Nachschlag, mit dem der Rückfall-Zeitgeber die Bewegung beendet, falls
 * `transitionend` ausbleibt (kein Größenwechsel, versteckter Tab,
 * `prefers-reduced-motion`).
 *
 * Er muss GRÖSSER als ein Bild sein, sonst käme er der Transition zuvor und
 * wäre genau das Zucken zurück, das der Wechsel auf das Ereignis abgeschafft
 * hat. 60ms sind rund vier Bilder bei 60Hz und immer noch unauffällig, wenn
 * er wirklich einmal greifen muss.
 */
export const MOTION_FALLBACK_MS = 60

/**
 * Ab hier ist es ein Zug und kein Klick — WAAGERECHT gemessen, in Client-Pixeln
 * seit dem `pointerdown`. Senkrecht zählt nicht: Ein Doppelklick wackelt gern ein
 * paar Pixel nach oben oder unten, und das ist keine Breitenänderung.
 *
 * NICHT `DUPLICATE_DRAG_THRESHOLD` (12) aus `canvasDuplicate.ts`: Die
 * beantwortet eine andere Frage („ab wann ist ein Alt-Zug ein Duplizieren")
 * und wäre hier toter Weg am Anfang jeder Geste. Ohne eine Schwelle schriebe
 * schon ein Klick auf das Handle ein Verhältnis, das sich um Bruchteile vom
 * bisherigen unterscheidet — und ein Doppelklick drei Werte statt einem.
 */
export const DRAG_THRESHOLD = 3

/**
 * Der **Magnet bei halbe/halbe**: Kommt die Hand auf so viele Pixel an die
 * Hälfte der Zeile heran, legt sich die Kante exakt darauf — in jedem Zug, aus
 * jeder Größe. Zieht man weiter, wird sie wieder frei (CONTEXT.md, Drag Handle;
 * Entscheidung mit Sven, 2026-09-03).
 *
 * Der Doppelklick bleibt der zweite Weg zur Hälfte. Der Magnet macht die
 * Hälfte im Zug ERREICHBAR — ohne ihn wäre sie eine Zahl, die niemand trifft.
 */
export const SNAP_HALF_PX = 20

/** Was ein Zug ergibt: die neue Größe und, falls sie gilt, das Verhältnis. */
export interface SplitDragResult {
  state: CanvasPanelState
  /**
   * Der Anteil der **Aufgabenliste** (0–1), oder `null`, wenn dieser Zug das
   * Verhältnis nicht anfasst — beim Zuklappen. Sonst käme die Liste beim
   * nächsten Öffnen auf ihrer Mindestbreite zurück statt dort, wo sie zuletzt
   * stand.
   */
  ratio: number | null
}

/** Nur die Maße, die die Rechnung braucht — kein `DOMRect` nötig. */
export interface SplitContainerRect {
  left: number
  width: number
}

/**
 * Das Verhältnis auf die Spanne klemmen, in der beide Spalten ihre
 * Mindestbreite behalten.
 *
 * **Reicht es für beide nicht** (Container schmaler als `LIST_MIN_WIDTH` +
 * `MIN_COLUMN_WIDTH`), gewinnt das Minimum des CANVAS und die Liste gibt nach.
 * Das ist kein Geschmack, sondern die Angleichung an CSS: Dort steht am Panel
 * `min-width: 320px` neben `max-width: calc(100% - 429px)`, und **`min-width`
 * schlägt `max-width` immer**. Ein Rückfall auf `DEFAULT_SPLIT_RATIO` — die
 * erste Fassung dieser Funktion — hätte genau im Grenzfall etwas anderes
 * ergeben als das Stylesheet, also zwei Quellen der Wahrheit, die dort
 * auseinanderlaufen, wo niemand hinsieht.
 *
 * Erreichbar ist der Fall nur im Web: Im Electron-Hauptfenster weicht die
 * Navigation, wenn es neben ihr nicht reicht (`shouldNavigationYield`), und
 * das Fenster wächst mit dem geteilten Canvas auf Liste + Canvas
 * (`mainWindowMinWidth`, #296).
 */
export function clampSplitRatio(ratio: number, containerWidth: number): number {
  if (!Number.isFinite(ratio) || containerWidth <= 0) return DEFAULT_SPLIT_RATIO

  // Der Canvas zuerst: Seine Obergrenze für die Liste steht fest, auch wenn
  // sie unter deren eigenes Minimum fällt.
  const max = (containerWidth - MIN_COLUMN_WIDTH) / containerWidth
  const min = LIST_MIN_WIDTH / containerWidth

  if (max <= 0) return 0
  // `min > max` heißt: Es reicht nicht für beide. Dann gilt `max` — der Canvas
  // behält seine 320px.
  if (min > max) return max

  return Math.min(Math.max(ratio, min), max)
}

/**
 * Was die Hand GERADE zeigt — die Kante folgt ihr, solange gezogen wird.
 *
 * **Verfolgen und Festschreiben sind zwei Fragen** (CONTEXT.md, Drag Handle:
 * „Der Canvas folgt dabei der Hand"). Diese hier beantwortet nur die erste;
 * was beim Loslassen daraus wird, sagt `commitSplitDrag`.
 *
 * | aus | Bedingung | wird |
 * |---|---|---|
 * | `maximized` | Liste erreicht `LIST_MIN_WIDTH` | `split`, Verhältnis am Zeiger |
 * | `closed` | — sofort — | `split`, Canvas ≥ `floor` |
 * | `split` | Liste unter `LIST_COLLAPSE_WIDTH` | `maximized`, Verhältnis unverändert |
 * | `split` | sonst | `split`, Canvas ≥ `floor`, Liste ≥ `LIST_MIN_WIDTH` |
 *
 * **Der Canvas kippt im Zug NICHT auf `closed`.** Er folgt der Hand bis auf
 * `floor` hinunter — die Streifenbreite des **Canvas-Anrisses**, wenn Elemente
 * liegen, sonst null — und bleibt dort stehen. Ob daraus `closed` wird,
 * entscheidet erst das Loslassen. Vorher kippte es unter 160px mitten im Zug
 * und animierte auf null, während die Hand noch zog.
 *
 * **Aus `closed` öffnet der erste Pixel.** Der Canvas ist eine Fläche und bei
 * jeder Breite eine Fläche; er kommt mit dem, was die Hand zeigt. Die
 * **Aufgabenliste** dagegen kommt aus `maximized` erst, wenn sie ihre
 * Mindestbreite hat: Sie ist eine Spalte aus Zeilen, schmaler hereinzukommen
 * hieße im nächsten Bild auf das Minimum zu springen.
 *
 * Die Hysterese (geöffnet ab dem ersten Pixel, geschlossen erst unter 160)
 * bleibt damit erhalten, sie liegt nur am Ende der Geste. Flattern kann
 * nichts, weil während des Zugs keine Größe kippt — außer nach `maximized`,
 * und dort ist der Wechsel eine Animation, kein Wackeln um eine Schwelle.
 *
 * **Der Magnet** (`SNAP_HALF_PX`): Nah an der Hälfte wird das Verhältnis exakt
 * 0,5 — aus jeder Größe, aber nur, wenn die Hälfte beiden Spalten ihre
 * Mindestbreite ließe.
 *
 * @param current der Zustand, aus dem heraus gezogen wird (wandert mit der Geste)
 * @param clientX die Zeigerlage
 * @param rect    die Zeile, in der Liste und Canvas nebeneinander liegen
 * @param floor   die kleinste Canvas-Breite im Zug: `CANVAS_PEEK_WIDTH` mit Anriss, sonst 0
 *
 * @example
 * trackSplitDrag('closed', 880, { left: 0, width: 1000 }, 77)
 * // → { state: 'split', ratio: 0.88 }   (120px Canvas — offen ab dem ersten Pixel)
 */
export function trackSplitDrag(
  current: CanvasPanelState,
  clientX: number,
  rect: SplitContainerRect,
  floor = 0,
): SplitDragResult {
  if (rect.width <= 0) return { state: current, ratio: null }

  // Wie breit die Liste würde, zöge man die Grenze genau hierhin. Die
  // Canvas-Seite rechnet `trackedRatio` — mit dem Boden statt einer Schwelle.
  const listWidth = clientX - rect.left

  if (current === 'maximized') {
    // Herausziehen: Erst wenn die Liste ihre Mindestbreite erreicht, ist sie
    // da. Bis dahin bleibt es maximiert.
    if (listWidth < LIST_MIN_WIDTH) return { state: 'maximized', ratio: null }
  } else if (current === 'split') {
    // Nach links durchgezogen: die Liste klappt zu. Das Verhältnis bleibt
    // bewusst unangetastet (siehe `SplitDragResult.ratio`).
    if (listWidth < LIST_COLLAPSE_WIDTH) return { state: 'maximized', ratio: null }
  }

  return { state: 'split', ratio: trackedRatio(listWidth, rect.width, floor) }
}

/**
 * Das Verhältnis im Zug: Canvas nicht unter `floor`, Liste nicht unter ihre
 * Mindestbreite, und bei der Hälfte der Magnet. Die Liste gewinnt, wenn beides
 * nicht geht — dieselbe Rangfolge wie `clampSplitRatio`, nur mit dem Boden des
 * Zugs statt der Mindestbreite des Canvas.
 */
function trackedRatio(listWidth: number, containerWidth: number, floor: number): number {
  const half = containerWidth / 2
  const halfFits = half >= LIST_MIN_WIDTH && containerWidth - half >= floor
  if (halfFits && Math.abs(listWidth - half) <= SNAP_HALF_PX) return DEFAULT_SPLIT_RATIO

  const maxList = containerWidth - floor
  const clampedList = Math.max(Math.min(listWidth, maxList), LIST_MIN_WIDTH)
  return Math.min(clampedList, containerWidth) / containerWidth
}

/**
 * Was beim Loslassen aus dem Verfolgten wird — der eine Moment, in dem eine
 * Größe kippen darf.
 *
 * | verfolgt | Canvas beim Loslassen | wird |
 * |---|---|---|
 * | `split` | unter `COLLAPSE_WIDTH` | `closed`, Verhältnis unverändert |
 * | `split` | sonst | `split`, geklemmt auf beide Mindestbreiten |
 * | `maximized` | — | unverändert |
 *
 * Der Weg zwischen 160 und 320 endet damit auf der Mindestbreite des Canvas —
 * wie bisher, nur dass die Hand ihn vorher wirklich dorthin ziehen konnte.
 * Der Magnet-Wert 0,5 liegt innerhalb der Klemmung und bleibt exakt stehen.
 *
 * @example
 * commitSplitDrag({ state: 'split', ratio: 0.9 }, { left: 0, width: 1000 })
 * // → { state: 'closed', ratio: null }   (100px Canvas: unter der Schwelle)
 */
export function commitSplitDrag(live: SplitDragResult, rect: SplitContainerRect): SplitDragResult {
  if (live.state !== 'split' || live.ratio === null || rect.width <= 0) return live
  const canvasWidth = (1 - live.ratio) * rect.width
  if (canvasWidth < COLLAPSE_WIDTH) return { state: 'closed', ratio: null }
  return { state: 'split', ratio: clampSplitRatio(live.ratio, rect.width) }
}
